import {Component, Inject, OnInit} from '@angular/core';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import {CommonService} from '../../../core/service/common.service';
import {yyyyMMdd} from '../../typeAliases';
import {MasterService} from '../../../core/service/master.service';
import {KeishaHibetsuFileService} from '../../../core/service/keisha-hibetsu-file.service';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {KeishaHibetsuFile} from '../../interfaces/keisha-hibetsu-file';
import {ShiyoulotService} from '../../../core/service/shiyoulot.service';
import {CsvUtilityService} from '../../../core/service/csv-utility.service';
import {CsvService} from '../../../core/service/csv.service';

@Component({
  selector: 'app-sagyou-kiroku-csv-export-dialog',
  templateUrl: './sagyou-kiroku-csv-export-dialog.component.html',
  styleUrls: ['./sagyou-kiroku-csv-export-dialog.component.scss']
})
export class SagyouKirokuCsvExportDialogComponent implements OnInit {
  readonly maxRange: number = 90; // 範囲の最大値
  // 初期値　to: 今日日付, from: maxRange日前
  fgSagyouKirokuDateRange = new FormGroup({
    fcStart: new FormControl({value: this.commonService.genOffsetDateWithStandardDateString(this.commonService.getDateWithString(), -this.maxRange), disabled: false}, Validators.required),
    fcEnd: new FormControl({value: this.commonService.genOffsetDateWithStandardDateString(this.commonService.getDateWithString(), 0), disabled: false}, Validators.required)
  });
  sagyouKirokuDateIsValid = true; // dateRangePicker使わない場合の日付の妥当性
  farmName = '';

  constructor(public commonService: CommonService,
              private masterService: MasterService,
              private keishaHibetsuFileService: KeishaHibetsuFileService,
              private shiyoulotService: ShiyoulotService,
              private csvUtilityService: CsvUtilityService,
              private csvService: CsvService,
              public matDialogRef: MatDialogRef<SagyouKirokuCsvExportDialogComponent>,
              @Inject(MAT_DIALOG_DATA) private farmId: string,
              ) { }

  ngOnInit(): void {
    this.farmName = this.commonService.getName(this.masterService.farmMaster, this.farmId);
  }

  /**
   * dateRangePickerを使う場合の日付変更イベント
   */
  onDateChanged() {
    const fromDate = this.fgSagyouKirokuDateRange.get('fcStart')?.value;
    const toDate = this.fgSagyouKirokuDateRange.get('fcEnd')?.value;

    this.sagyouKirokuDateIsValid = this.commonService.fromToValidating(fromDate, toDate);
  }

  /**
   * CSV出力ボタン押下時の処理
   */
  async onClickExportCSV() {
    const fromDate = this.fgSagyouKirokuDateRange.get('fcStart')?.value;
    const toDate = this.fgSagyouKirokuDateRange.get('fcEnd')?.value;

    // 鶏舎マスタで鶏舎名を昇順にソートして、keishaIdListを作成
    // ソートはcsvの表示順序を決めるため
    const filteredKeishaMaster = this.masterService.keishaMaster.filter(keisha => keisha.isDeleted === false && keisha.farmId === this.farmId);
    const keishaIdList = this.commonService.sortMultiKeys(filteredKeishaMaster, [{key: 'name', direction: 1}])
      .map(keisha => keisha.id);

    // 日付毎の鶏舎毎の日別ファイルを取得
    const dateToKeishaHibetsuFilesMap = await this.genDateToKeishaHibetsuFilesMap(keishaIdList, fromDate, toDate);

    // 作業記録をCSV出力
    this.exportSagyouKirokuCSV(dateToKeishaHibetsuFilesMap, keishaIdList);

    // ダイアログを閉じる
    this.commonService.onClick(this.matDialogRef);
  }

  /**
   * 日付毎の鶏舎毎の日別ファイルを取得
   *
   * @param keishaIdList
   * @param fromDate
   * @param toDate
   */
  async genDateToKeishaHibetsuFilesMap(keishaIdList: string[], fromDate: yyyyMMdd, toDate: yyyyMMdd) {
    // 鶏舎毎の日別ファイルを取得
    const promiseArray = [];
    for (const keishaId of keishaIdList) {
      // TODO 鶏舎日別ファイルの現在羽数を使わないため、getKeishaHibetsuFileUpdatedGenzaiWasuuを使わない
      const promise = this.keishaHibetsuFileService.getKeishaHibetsuFile(
        keishaId,
        this.commonService.formatDateRangePickerDate(fromDate),
        this.commonService.formatDateRangePickerDate(toDate));
      promiseArray.push(promise);
    }

    // Promise.allで全てのPromiseが完了したら、key: 日付, value: hibetsuFileListとしてMapを作成
    const keishaHibetsuFileDocs = await Promise.all(promiseArray);

    // key: 日付、value: keishaHibetsuFiles
    const dateToKeishaHibetsuFilesMap = new Map<string, KeishaHibetsuFile[]>();

    keishaHibetsuFileDocs.forEach((keishaHibetsuFileDoc) => {
      const keishaHibetsuFiles = keishaHibetsuFileDoc.docs.map(doc =>
        this.commonService.updateKeishaHibetsuFileV1ToV2(doc.data())
      ) as KeishaHibetsuFile[];

      keishaHibetsuFiles.forEach((keishaHibetsuFile) => {
        const key = keishaHibetsuFile.id;
        const keishaHibetsuFilesByDate = dateToKeishaHibetsuFilesMap.get(key);
        if (keishaHibetsuFilesByDate) {
          keishaHibetsuFilesByDate.push(keishaHibetsuFile);
        } else {
          dateToKeishaHibetsuFilesMap.set(key, [keishaHibetsuFile]);
        }
      });
    });

    return dateToKeishaHibetsuFilesMap;
  }

  /**
   * 鶏舎の作業記録をCSV出力
   */
  exportSagyouKirokuCSV(dateToKeishaHibetsuFilesMap: Map<string, KeishaHibetsuFile[]>, keishaIdList: string[]) {
    const sagyouKirokuCSVArr = [...dateToKeishaHibetsuFilesMap.keys()].map((key) => {
      // 日毎の各鶏舎の日別ファイルの配列
      const keishaHibetsuFiles = dateToKeishaHibetsuFilesMap.get(key);
      // csv行
      let sagyouKirokuCSVRow: {[key: string]: string} = {
        key: key, // 日付
      };
      keishaIdList.forEach((keishaId) => {
        // 特定の鶏舎の日別ファイル
        const keishaHibetsuFile = keishaHibetsuFiles?.find(keishaHibetsuFile => keishaHibetsuFile.keishaId === keishaId);
        // 特定の鶏舎の作業記録を生成
        const sagyouKiroku = this.csvUtilityService.genSagyouKirokuForCSVRow(keishaId, keishaHibetsuFile);
        // 作業記録をCSV行に追加
        sagyouKirokuCSVRow = {...sagyouKirokuCSVRow, ...sagyouKiroku};
      });
      return sagyouKirokuCSVRow;
    });

    const farmName = this.commonService.getName(this.masterService.farmMaster, this.farmId);

    this.csvService.exportCsv(
      sagyouKirokuCSVArr,
      this.csvUtilityService.genSagyouKirokuPropertiesForCSV(keishaIdList),
      'horizontal',
      '農場作業記録_' + farmName ?? ''
    );
  }

}
